import type { RosterPlayerForInjury } from './playerLookup';
import { extractAssignPlayerPositionOnly, normalizePositionKey } from './parsers';
import { findPlayersForInjury, playerRefToIdentifier } from './playerLookup';

const WORD_INNINGS: Record<string, number> = {
  one: 1,
  a: 1,
  single: 1,
  two: 2,
  couple: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
};

/** Map spoken / written phrases to defensive position keys for baseball & softball */
const BASE_DEF_ALIASES: Record<string, string> = {
  pitcher: 'P',
  pitching: 'P',
  catcher: 'C',
  cath: 'C',
  'first base': '1B',
  'first baseman': '1B',
  '1st base': '1B',
  'second base': '2B',
  'second baseman': '2B',
  '2nd base': '2B',
  'third base': '3B',
  'third baseman': '3B',
  '3rd base': '3B',
  shortstop: 'SS',
  'left field': 'LF',
  'left fielder': 'LF',
  'center field': 'CF',
  'center fielder': 'CF',
  centre: 'CF',
  'right field': 'RF',
  'right fielder': 'RF',
  dh: 'DH',
  'designated hitter': 'DH',
};

export function normalizeBaseballDefenseKey(positionPart: string): string {
  const raw = positionPart.trim().replace(/^\s*the\s+/i, '').replace(/\s+position\s*$/i, '').trim();
  const lowered = raw.toLowerCase();
  if (BASE_DEF_ALIASES[lowered]) return BASE_DEF_ALIASES[lowered];
  const compact = lowered.replace(/\s+/g, ' ');
  if (BASE_DEF_ALIASES[compact]) return BASE_DEF_ALIASES[compact];
  const viaFootball = normalizePositionKey(raw);
  if (/^(P|C|1B|2B|3B|SS|LF|CF|RF|DH)$/.test(viaFootball)) return viaFootball;
  return raw.toUpperCase().replace(/\s+/g, '');
}

function splitPitchingSegments(text: string): string[] {
  return text
    .split(/\s*(?:,|;|(?:\s+then\s+)|(?:\s+followed\s+by\s+)|(?:\s+and\s+then\s+)|(?:\s+relieved\s+by\s+))\s*/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function wordOrDigitsToNum(s: string): number | undefined {
  const lower = s.toLowerCase().trim();
  if (WORD_INNINGS[lower] != null) return WORD_INNINGS[lower];
  const n = parseInt(lower, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Infer how many innings a clause covers (relative span, not absolute innings).
 */
export function extractRelativeInningSpan(segment: string): number {
  const lower = segment.toLowerCase();

  const firstN = lower.match(/\bfirst\s+(one|two|three|four|five|six|seven|\d+)\s+innings?\b/);
  if (firstN) {
    const v = wordOrDigitsToNum(firstN[1]);
    if (v != null) return v;
  }

  const nInnings = lower.match(
    /\b(one|two|three|four|five|six|seven|a|\d+)\s+(?:full\s+)?(?:complete\s+)?innings?\b/
  );
  if (nInnings) {
    const v = wordOrDigitsToNum(nInnings[1]);
    if (v != null) return v;
  }

  const range = lower.match(/\binnings?\s+(\d+)\s*(?:-|–|through|to)\s*(\d+)\b/);
  if (range) {
    const a = parseInt(range[1], 10);
    const b = parseInt(range[2], 10);
    if (Number.isFinite(a) && Number.isFinite(b)) return Math.max(1, b - a + 1);
  }

  const forRange = lower.match(/\b(?:for|throws|pitch(?:es)?)\s+(\d+)\s*(?:-|–|through|to)\s*(\d+)\b/);
  if (forRange) {
    const a = parseInt(forRange[1], 10);
    const b = parseInt(forRange[2], 10);
    if (Number.isFinite(a) && Number.isFinite(b)) return Math.max(1, b - a + 1);
  }

  return 1;
}

export function findPlayersInSegment(roster: RosterPlayerForInjury[], segment: string): RosterPlayerForInjury[] {
  const lower = segment.toLowerCase();
  const fullMatches = roster.filter((p) => {
    const full = `${p.first_name} ${p.last_name}`.toLowerCase().trim();
    return full.length > 2 && lower.includes(full);
  });
  if (fullMatches.length === 1) return fullMatches;
  if (fullMatches.length > 1) return fullMatches;

  const tokens = segment.match(/#?\d{1,3}|[A-Za-z][A-Za-z'\-]+/g) || [];
  for (const t of tokens) {
    if (/^#?\d{1,3}$/.test(t)) {
      const id = playerRefToIdentifier(t);
      const m = findPlayersForInjury(roster, id);
      if (m.length === 1) return m;
      if (m.length > 1) return m;
    }
  }
  for (const t of tokens) {
    if (/^\d+$/.test(t)) continue;
    const id = playerRefToIdentifier(t);
    const m = findPlayersForInjury(roster, id);
    if (m.length === 1) return m;
    if (m.length > 1) return m;
  }
  return [];
}

export function looksLikePitchingRotationMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    /\bpitching\s+(rotation|order)\b/.test(lower) ||
    /\b(starting\s+)?pitch(?:er)?\b/.test(lower) ||
    /\brelieved\s+by\b/.test(lower) ||
    /\bmound\b/.test(lower) ||
    /\b(who(?:'s|\s+is)\s+pitching)\b/.test(lower) ||
    (/\b(one|two|three|four|five|six)\s+innings?\b/.test(lower) && /\b(and\s+then|then|followed\s+by)\b/.test(lower))
  );
}

export interface PitchingRotationBlock {
  playerId: string;
  playerLabel: string;
  fromInning: number;
  toInning: number;
}

/**
 * Sequential pitching: each clause gets the next contiguous innings (`span` wide).
 */
export function parseSequentialPitchingRotation(
  text: string,
  roster: RosterPlayerForInjury[],
  maxRegulationInnings: number
): PitchingRotationBlock[] | null {
  const segments = splitPitchingSegments(text);
  const blocks: PitchingRotationBlock[] = [];
  let nextInning = 1;

  for (const seg of segments) {
    if (
      /\b(who|pitching\s+rotation|set\s+|update\s+|for\s+(?:the\s+)?game)\b/i.test(seg) &&
      !/[A-Za-z][a-z]+\s+[A-Za-z][a-z]+/.test(seg)
    ) {
      continue;
    }
    let span = extractRelativeInningSpan(seg);

    let restOfGame =
      /\brest\s+of\s+(?:the\s+)?(?:game|day)\b/i.test(seg) || /\bfinal\s+\d+\s+innings?\b/i.test(seg);
    const matches = findPlayersInSegment(roster, seg);
    if (matches.length !== 1) return null;

    if (restOfGame) {
      span = Math.max(1, maxRegulationInnings - nextInning + 1);
    }

    const from = nextInning;
    const to = Math.min(from + span - 1, maxRegulationInnings);
    if (from > maxRegulationInnings) break;

    const p = matches[0];
    const label = `${p.first_name} ${p.last_name}`.trim() + (p.number != null ? ` #${p.number}` : '');
    blocks.push({ playerId: p.id, playerLabel: label, fromInning: from, toInning: to });
    nextInning = to + 1;
  }

  if (blocks.length === 0) return null;
  return blocks;
}

export interface ParsedBattingSlot {
  battingOrder: number;
  nameTokens: string;
}

/** "1. Smith … 2) Jones … 4 Taylor" style */
export function parseBattingOrderList(text: string): ParsedBattingSlot[] | null {
  const slots: ParsedBattingSlot[] = [];
  const withStop = /(\d{1,2})\s*[.)]\s*([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)/gi;
  let m: RegExpExecArray | null;
  while ((m = withStop.exec(text)) !== null) {
    const order = parseInt(m[1], 10);
    if (order >= 1 && order <= 20) slots.push({ battingOrder: order, nameTokens: m[2].trim() });
  }
  if (slots.length === 0) {
    const spaced = /(\d{1,2})\s+([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)\b/gi;
    while ((m = spaced.exec(text)) !== null) {
      const order = parseInt(m[1], 10);
      if (order >= 1 && order <= 20) slots.push({ battingOrder: order, nameTokens: m[2].trim() });
    }
  }
  const sorted = [...new Map(slots.map((s) => [s.battingOrder, s])).values()].sort(
    (a, b) => a.battingOrder - b.battingOrder
  );
  return sorted.length > 0 ? sorted : null;
}

export interface ResolvedBattingEntry {
  batting_order: number;
  player_id: string;
  label: string;
}

export function resolveBattingSlots(
  slots: ParsedBattingSlot[],
  roster: RosterPlayerForInjury[]
): { ok: ResolvedBattingEntry[] } | { error: string } {
  const out: ResolvedBattingEntry[] = [];
  for (const s of slots) {
    const matches = findPlayersInSegment(roster, s.nameTokens);
    if (matches.length === 0) return { error: `I couldn't match **${s.nameTokens}** to the roster.` };
    if (matches.length > 1)
      return { error: `More than one player matches **${s.nameTokens}** — use a jersey number or full name.` };
    const p = matches[0];
    const label = `${p.first_name} ${p.last_name}`.trim();
    out.push({ batting_order: s.battingOrder, player_id: p.id, label });
  }
  return { ok: out };
}

const INNING_IN_MESSAGE = /\b(?:inning|innings)\s+(\d{1,2})\b/i;

/** Extract inning 1.. from "inning 4" / "bottom of the 7th" simplified */
export function extractSingleInningHint(text: string): number | undefined {
  const m = text.match(/\b(?:inning|inn)\s*(\d{1,2})\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 30) return n;
  }
  const top = text.match(/\b(?:top|bottom)\s+of\s+(?:the\s+)?(\d{1,2})\b/i);
  if (top) {
    const n = parseInt(top[1], 10);
    if (n >= 1 && n <= 30) return n;
  }
  return undefined;
}

export function extractBaseballSinglePositionAssignment(
  text: string
): { playerRef: string; positionKey: string } | null {
  const withFormationBlocked = extractAssignPlayerPositionOnly(text);
  if (withFormationBlocked) {
    const pos = normalizeBaseballDefenseKey(withFormationBlocked.positionKey);
    // Reject any numeric token (single or multi-digit, optional punctuation) —
    // "move Stanley up to 7" means batting slot 7, never a fielding position.
    if (/^\d+[.):]?$/.test(pos)) return null;
    return { playerRef: withFormationBlocked.playerRef, positionKey: pos };
  }
  return null;
}

export function isLikelyBattingOrderOnlyMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    /\bbatting\s+order\b/.test(lower) ||
    /\blineup\b/.test(lower) ||
    /\bset\s+(?:the\s+)?(?:batting\s+)?order\b/.test(lower) ||
    (/\b\d{1,2}\s*[).:]/i.test(text) && /\b(hit|hits|cleanup|leadoff|batting)\b/i.test(lower))
  );
}
